import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserPlus, X, Loader2, Bell } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  profileImageUrl: string | null;
}

interface FriendRequest {
  id: string;
  requesterId: string;
  addresseeId: string;
  status: string;
  createdAt: string;
  requester: User;
}

function getInitials(user: User): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || "?";
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatName(user: User): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || "Usuario";
}

export function FriendRequests() {
  const { toast } = useToast();

  const { data: requests = [], isLoading } = useQuery<FriendRequest[]>({
    queryKey: ['/api/friends/requests'],
  });

  const acceptMutation = useMutation({
    mutationFn: async (requestId: string) => {
      await apiRequest("POST", `/api/friends/accept/${requestId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friends/requests'] });
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
      toast({
        title: "¡Solicitud aceptada!",
        description: "Ahora son amigos.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo aceptar la solicitud.",
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (requestId: string) => {
      await apiRequest("POST", `/api/friends/reject/${requestId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friends/requests'] });
      toast({
        title: "Solicitud rechazada",
        description: "La solicitud ha sido rechazada.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo rechazar la solicitud.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return null;
  }

  if (requests.length === 0) {
    return null;
  }

  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4 text-amber-500" />
          Solicitudes de Amistad
          <Badge variant="secondary" className="bg-amber-500/20 text-amber-700 dark:text-amber-400 border-0">
            {requests.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {requests.map((request) => (
          <div key={request.id} className="flex items-center gap-3 p-3 rounded-lg bg-background border">
            <Avatar className="h-10 w-10 border border-amber-500/30">
              <AvatarImage src={request.requester.profileImageUrl || undefined} />
              <AvatarFallback className="bg-amber-500/20 text-amber-700 dark:text-amber-400 text-sm font-semibold">
                {getInitials(request.requester)}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate" data-testid={`text-request-name-${request.id}`}>
                {formatName(request.requester)}
              </p>
              <p className="text-xs text-muted-foreground">
                Quiere ser tu amigo
              </p>
            </div>

            <div className="flex gap-1">
              <Button
                variant="default"
                size="sm"
                className="h-8"
                onClick={() => acceptMutation.mutate(request.id)}
                disabled={acceptMutation.isPending || rejectMutation.isPending}
                data-testid={`button-accept-request-${request.id}`}
              >
                {acceptMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => rejectMutation.mutate(request.id)}
                disabled={acceptMutation.isPending || rejectMutation.isPending}
                data-testid={`button-reject-request-${request.id}`}
              >
                {rejectMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
